import { CellProgressCounts, CollectionDataRecord, Instrument, InstrumentStateValue, ReusableCell, RunDataRecord } from "../model/instrument-model";
import * as API from "../api";
import * as _ from "lodash"
import { RunStateValue } from "../model/run-qc-model";
import { durationString } from "../../core/utils/smrt-link-date-format";
import { InstrumentType, instrumentTypeBySerial, isRevio } from "../model/instrument-type-model";

// --- The UI-tier model classes for the instruments table
export interface InstrumentTableRow {
    serial: string
    name: string
    instrumentType: InstrumentType
    state: InstrumentStateValue
    timeUntilPreload: string
    runInfos?: InstrumentTableRunInfo[]
    reusableCells?: ReusableCell[]
    alarmMessage?: string
    updatedAt: string
    fullRowError?: string // will replace entire row if present

}

export interface InstrumentTableRunInfo {
    runData: RunDataRecord
    runId: string
    runState: RunStateValue
    cellProgressCounts: CellProgressCounts
    totalCells: number
    runCompletion: string
    collections: CollectionDataRecord[]
}

export const runInfoFromInstrument = (instrument: InstrumentTableRow, runId: string) => {
    if ( instrument) {
        if (runId) {
            return instrument.runInfos.find( runInfo => runInfo.runId === runId)
        }
        if (instrument.runInfos.length === 1) {
            return instrument.runInfos[0]
        }
    }
   return null
}

export const getInstrumentTableRows = ():Promise<InstrumentTableRow[]> => {
    return API.getInstruments().then( response => {
        if(response.ok) {
            const instruments = response.data
            return instruments.map(instrumentToInstrumentTableRow)
        }
        else {
            const { status, originalError } = response
            throw new Error(`Server Error.  Status: ${status}. ${originalError.message}`)
        }
    })
}

export const getInstrumentTableRowForRun = (runId: string ):Promise<InstrumentTableRow> => {
    return getInstrumentTableRows().then( ( instruments ) => {
        for (let instrument of instruments) {
            for ( let runInfo of instrument.runInfos) {
                if (runInfo.runId === runId) {
                    return instrument
                }
            }
        }
        return null
    })
}

export const instrumentToInstrumentTableRow =  (instrument: Instrument): InstrumentTableRow => {

    try {
        const { state, serial } = instrument

        let name = state ? state.instrumentName : undefined

        if (!name || name.length === 0) {
             name = serial
        }
        let instrumentType = instrument.state.instrumentType
        if (!instrumentType) {
            instrumentType = instrumentTypeBySerial(serial)
        }


        let runInfos: InstrumentTableRunInfo[] = []
        if (state.runs) {
            runInfos = state.runs.map( runData => runDataToRunInfo(runData, instrument) )
        }
        let hasAbortingRuns = false
        let hasOngoingRuns = false
        if (runInfos && runInfos.length > 0) {
            hasAbortingRuns = runInfos.findIndex( run => run.runState === "Aborting") !== -1
            const onGoingStates: RunStateValue[] = ["Completing","Paused", "Ready", "Running","Starting"]
            hasOngoingRuns = runInfos.findIndex( run => onGoingStates.includes(run.runState) ) !== -1
        }
        const isStoppingState = hasAbortingRuns && !hasOngoingRuns
        const instrumentState: InstrumentStateValue = isStoppingState ?
            "Stopping" :
            instrument.state.state

        return {
            name: name,
            serial: instrument.serial,
            instrumentType: instrumentType,
            state: instrumentState,
            runInfos: runInfos,
            alarmMessage: alarmMessage(instrument),
            timeUntilPreload: timeUntilPreload(instrument),
            updatedAt: instrument.updatedAt,
            reusableCells: instrument.state.reusableCells
        }
    }
    catch (error) {
        const { serial } = instrument
        const serialClause = serial ? `for instrument with serial ${serial}` : ""

        return {
            name: null,
            serial: null,
            instrumentType: null,
            state: null,
            runInfos: [],
            alarmMessage: null,
            timeUntilPreload: null,
            updatedAt: null,
            fullRowError: `Software error ${serialClause}: ${(error as Error).message})`
        }
    }

}

const runDataToRunInfo = (runData: RunDataRecord, instrument: Instrument): InstrumentTableRunInfo => {

    if (!runData) return undefined

    const cellProgressCounts = isInstrumentRevio(instrument)?
        runData.cellProgressCounts :
        cellProgressCountsForSequel(runData)

    const totalCells = isInstrumentRevio(instrument) ?
        runData.numCells :
        runData.collections.length

    const secsRemaining = runData.estimatedSecondsRemaining

    return {
        runData: runData,
        runId: runData.runId,
        runState: runData.state,
        cellProgressCounts:  cellProgressCounts,
        totalCells: totalCells,
        runCompletion: runCompletionString(runData.state, runData.completedAt,secsRemaining),
        collections: runData.collections
    }
}

const cellProgressCountsForSequel = (runData: RunDataRecord): CellProgressCounts => {

    if (!runData || !runData.collections) {
        return null
    }

    const pendingStates = ["Ready", "Pending" ]
    const loadingStates = [ "Initializing", "InPrep", "PredictiveLoading"]
    const sequencingStates = [ "SocketDiagnostics", "Aligning", "Aligned", "Calibrating",
        "ReadyToCalibrate", "CalibrationComplete", "ReadyToAcquire", "Acquiring", "FinishingAnalysis"]
    const abortedStates = ["Aborting", "Aborted", "Stopped" ]
    const failedstates = ["Failed", "Error", "Unknown"]
    const notCompleteStates =
        [...pendingStates,...loadingStates,...sequencingStates, ...abortedStates, ...failedstates ]

    return {
        pending: runData.collections.filter( col => pendingStates.includes(col.state)).length,
        loading: runData.collections.filter( col => loadingStates.includes(col.state)).length,
        sequencing: runData.collections.filter( col => sequencingStates.includes(col.state)).length,
        complete: runData.collections.filter( col => !( notCompleteStates.includes(col.state)) ).length,
        aborted: runData.collections.filter( col => abortedStates.includes(col.state)).length,
        failed: runData.collections.filter( col => failedstates.includes(col.state)).length,
    }
}

const timeUntilPreload =  (instrument: Instrument): string => {
    const hasRunningRun = (instrument: Instrument) => {
        for (let run of instrument.state.runs) {
            if (run.state === "Running") {
                return true
            }
        }
        return false
    }
    if (hasRunningRun(instrument)) {
        const time = instrument.state.readyToLoadNextRunTimeSeconds
        if (time === 0) {
            return "Available"
        }
        if (time > 0) {
            return durationString(time)
        }
    }
    return null
}

export const runCompletionString = (state: RunStateValue, completedAt: string, secsRemaining: number): string | null => {

    const completedAtMs = Date.parse(completedAt)

    if (secsRemaining) {
        return `In ${durationString(secsRemaining)}`
    }
    else {
        const now = new Date()
        const idleTime = now.getTime() - completedAtMs
        const MS_PER_HOUR = 1000 * 60 * 60
        const idleTimeHours = idleTime / MS_PER_HOUR
        if ( idleTimeHours <= 48) {
                const whatHappened = () => {
                    switch (state) {
                        case ("Complete"): return "Completed"
                        case ("Aborted"): return "Canceled"
                        case ("Paused"): return "Paused"
                        case ("Terminated"): return "Stopped"
                    }
                    return null
                }
            if (whatHappened() !== null) {
                const idleTimeSecs = idleTime / 1000
                return `${whatHappened()} ${durationString(idleTimeSecs)} ago `

            }
        }
        // if idle > 48 hours return nothing
        return null
    }
}

const alarmMessage = (instrument: Instrument) => {
    if (instrument && instrument.state) {
        const { systemAlarms} = instrument.state
        if (!systemAlarms) {
            return undefined
        }
        const { alarms } = systemAlarms
        if (alarms && alarms.length > 0) {
            const severeAlarms = alarms.filter( alarm => alarm.severity !== "WARNING")
            if (severeAlarms.length > 0 ) {
                if (severeAlarms.length === 1) {
                    return severeAlarms[0].publicMessage
                }
                return "Multiple Alarms"
            }
        }
    }
    return undefined
}

const isInstrumentRevio = (instrument: Instrument): boolean => {
    const instrumentType = instrumentTypeBySerial(instrument.serial)
    return isRevio(instrumentType)
}

export const runCompletionComparator = (value1, value2, node1, node2) => {

    const instrument1: InstrumentTableRow = node1.data
    const instrument2: InstrumentTableRow = node2.dat
    const runs1 = instrument1.runInfos.map( runInfo => runInfo.runData)
    const runs2 = instrument2.runInfos.map( runInfo => runInfo.runData)
    if (_.isEmpty(runs1) && _.isEmpty(runs2)) {
        return 0
    }
    if (_.isEmpty(runs1)) { return -1 }
    if (_.isEmpty(runs2)) { return 1 }

    const remaining1 = runs1.map( run => run.estimatedSecondsRemaining || 0)
    const remaining2 = runs2.map( run => run.estimatedSecondsRemaining || 0)
    const maxRemaining1 = Math.max(...remaining1)
    const maxRemaining2 = Math.max(...remaining2)
    if (maxRemaining1 === maxRemaining2) {
        return 0
    }
    return maxRemaining1 > maxRemaining2 ? 1 : -1
}
